//frontend/src/pages/SelectDateTime.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Container, 
    Typography, 
    Box, 
    Button, 
    Grid, 
    Paper,
    CircularProgress,
    Alert
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { format, addMinutes, isBefore, startOfToday } from 'date-fns';
import { useBooking } from '../context/BookingContext';
import axiosInstance from '../utils/axios';
import { useTranslation } from 'react-i18next';


export default function SelectDateTime() {
    const { t } = useTranslation();
    
    const { bookingData, setBookingData, navigateToStep } = useBooking();
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!bookingData.service && !bookingData.specialist) {
            navigateToStep('service');
        }
    }, []);

    useEffect(() => {
        if (selectedDate && (bookingData.service && bookingData.specialist)) {
            fetchAvailableSlots();
        }
    }, [selectedDate, bookingData.service, bookingData.specialist]);

    const fetchAvailableSlots = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get(`/appointments/availability/${bookingData.specialist._id}`, {
                params: {
                    date: selectedDate.toISOString(),
                    serviceId: bookingData.service._id,
                    duration: bookingData.service.duration
                }
            });
            setAvailableSlots(response.data.availableSlots);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch available slots');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
        setSelectedTime(null);
    };

    const handleTimeSelect = (time) => {
        setSelectedTime(new Date(time));
    };

    const handleConfirm = () => {
        if (!selectedDate || !selectedTime) {
            return;
        }
        
        // Добавляем расчет времени окончания
        const endTime = addMinutes(new Date(selectedTime), bookingData.service.duration);
        
        setBookingData(prev => ({
            ...prev,
            dateTime: selectedTime,
            endTime: endTime
        }));
        
        // Переходим сразу к следующему шагу
        navigate('/confirmation');
    };

    const shouldDisableDate = (date) => {
        if (isBefore(date, startOfToday())) return true;
        
        if (!bookingData.specialist?.workingHours) return true;
        
        const dayOfWeek = date.getDay();
        const workDay = bookingData.specialist.workingHours.find(
            wh => wh.dayOfWeek === (dayOfWeek === 0 ? 7 : dayOfWeek)
        );
        
        return !workDay?.isWorking;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container maxWidth="md">
                <Typography variant="h4" gutterBottom align="center">
                {t('booking.datetime.title')}
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={7}>
                        <Paper elevation={3}>
                            <StaticDatePicker
                                displayStaticWrapperAs="desktop"
                                orientation="landscape"
                                value={selectedDate}
                                onChange={handleDateChange}
                                shouldDisableDate={shouldDisableDate}
                                minDate={new Date()}
                                slotProps={{
                                    actionBar: { actions: [] }
                                }}
                            />
                        </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={5}>
                        <Paper elevation={3} sx={{ p: 2, height: '100%', minHeight: 400 }}>
                            <Typography variant="h6" gutterBottom>
                            {t('booking.datetime.availableSlots')}
                            </Typography>
                            
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    maxHeight: 300, 
                                    overflow: 'auto',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: 1 
                                }}>
                                    {availableSlots.map((slot) => (
                                        <Button
                                            key={slot}
                                            variant={selectedTime?.toISOString() === slot ? "contained" : "outlined"}
                                            onClick={() => handleTimeSelect(slot)}
                                            size="large"
                                        >
                                            {format(new Date(slot), 'HH:mm')}
                                        </Button>
                                    ))}
                                </Box>
                            )}

                            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => navigate(-1)}
                                >
    {t('common.back')}
    </Button>
                                <Button
                                    variant="contained"
                                    disabled={!selectedDate || !selectedTime || loading}
                                    onClick={handleConfirm}
                                >
    {t('common.next')}
    </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </LocalizationProvider>
    );
}